import * as Dialog from "@radix-ui/react-dialog";
import { ReactNode, useEffect, useState } from "react";

import { CloseMD } from "@/icons";
import { useResponsiveness } from "@/providers/context";
import { cn } from "@/utils";

interface DrawerProps {
  title?: string;
  children?: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  dataTestId?: string;
  className?: string;
  titleClassName?: string;

  // Use default actions
  onCancel?: () => void; // Cancel the modal
  hideClose?: boolean; // Hide X button
}

export const Drawer = ({
  title,
  hideClose,
  children,
  onCancel,
  open,
  onOpenChange,
  dataTestId,
  className,
  titleClassName,
}: DrawerProps) => {
  const { drawerWidth, drawerMargin, showOverlay } = useResponsiveness();

  const [isMounted, setIsMounted] = useState(open); // Handles mounting
  const [isAnimating, setIsAnimating] = useState(false); // Controls the transition phase

  useEffect(() => {
    let timer = null;
    if (open) {
      // Set mounted and start animating immediately
      setIsMounted(true);
      timer = setTimeout(() => {
        setIsAnimating(true);
      }, 0); // No need for delay, just ensure it's on the next tick
    } else {
      // Animate out, then unmount after animation completes
      setIsAnimating(false);
      timer = setTimeout(() => setIsMounted(false), 300); // Use the duration from your CSS
    }

    return () => clearTimeout(timer);
  }, [open]);

  const isModal = !drawerMargin;

  return (
    <Dialog.Root open={isMounted} onOpenChange={onOpenChange} modal={isModal}>
      {showOverlay && (
        <Dialog.Overlay className="fixed inset-0 z-[50] bg-[#212327] bg-opacity-30 transition-opacity delay-300 ease-in" />
      )}
      <Dialog.Portal>
        <Dialog.Content
          data-testid={dataTestId}
          className={`bg-surface-primary border-border absolute right-0 top-0 z-[60] flex h-full transform flex-col border-l-[1px] border-solid px-1 transition-transform duration-300 ease-in-out focus:outline-none ${
            isAnimating ? "translate-x-0" : "translate-x-full"
          }`}
          style={{ width: drawerWidth }}
          onPointerDownOutside={(e) => {
            e.preventDefault();
          }}
          onInteractOutside={(e) => {
            e.preventDefault();
          }}
        >
          {title && (
            <Dialog.Title
              className={cn("w-full px-6 pb-[20px] pt-[32px]", titleClassName)}
            >
              <div className="p1-bold text-item-primary max-w-full overflow-hidden overflow-ellipsis whitespace-nowrap pr-[28px]">
                {title}
              </div>
              {!hideClose && (
                <Dialog.Close
                  asChild
                  className="text-item-secondary hover:text-item-tertiary absolute right-[28px] top-[32px] cursor-pointer rounded-3xl text-[18px]"
                  onClick={onCancel}
                >
                  <CloseMD className="h-[24px] w-[24px]" />
                </Dialog.Close>
              )}
            </Dialog.Title>
          )}
          <div className={cn("h-full overflow-auto px-6", className)}>
            {children}
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
